import { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { api } from '../lib/api';

interface AudioRecorderProps {
  /** Chamado quando a transcrição volta. Recebe o texto. */
  onTranscribed: (text: string) => void;
  /** Chamado em caso de erro (ex: permissão negada, sem chave de API). */
  onError: (message: string) => void;
  disabled?: boolean;
}

type State = 'idle' | 'recording' | 'transcribing';

export function AudioRecorder({ onTranscribed, onError, disabled }: AudioRecorderProps) {
  const [state, setState] = useState<State>('idle');
  const [elapsed, setElapsed] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number>(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    return () => {
      // Cleanup ao desmontar
      tickRef.current && clearInterval(tickRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const start = async () => {
    if (disabled || state !== 'idle') return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        // Para o stream
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (tickRef.current) {
          clearInterval(tickRef.current);
          tickRef.current = null;
        }

        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size === 0) {
          setState('idle');
          return;
        }

        setState('transcribing');
        try {
          const { text } = await api.transcribe(blob);
          onTranscribed(text);
        } catch (err) {
          onError(err instanceof Error ? err.message : String(err));
        } finally {
          setState('idle');
          setElapsed(0);
        }
      };

      recorder.start();
      startedAtRef.current = Date.now();
      setElapsed(0);
      tickRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }, 250);
      setState('recording');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      onError(msg.includes('Permission') ? 'Permissão de microfone negada' : msg);
      setState('idle');
    }
  };

  const stop = () => {
    if (state === 'recording' && mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
  };

  const formatElapsed = (s: number) => {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m.toString().padStart(2, '0')}:${r.toString().padStart(2, '0')}`;
  };

  if (state === 'transcribing') {
    return (
      <button type="button" disabled className="btn">
        <Loader2 size={14} className="animate-spin text-blue-600" />
        Transcrevendo...
      </button>
    );
  }

  if (state === 'recording') {
    return (
      <button
        type="button"
        onClick={stop}
        className="btn bg-red-600 border-red-400 text-white animate-pulse"
        title="Clique para parar"
      >
        <MicOff size={14} />
        {formatElapsed(elapsed)}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      disabled={disabled}
      className="btn"
      title="Gravar áudio (será transcrito para texto)"
    >
      <Mic size={14} />
    </button>
  );
}
